/**
 * TrialPurgeDemoDialog — Confirmation dialog for clearing demo data
 * and transitioning into real organization setup.
 *
 * Preserves: user account, org shell, trial state, preferences.
 * Purges: demo-tagged entities (members, events, campaigns, companies).
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useTrialOrientation } from "@/hooks/useTrialOrientation";
import { useOrg } from "@/contexts/OrgContext";
import { supabase } from "@/integrations/supabase/client";
import { DEMO_DATA_TAG } from "@/lib/trialOrientation";
import { toast } from "sonner";

interface TrialPurgeDemoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function TrialPurgeDemoDialog({ open, onOpenChange }: TrialPurgeDemoDialogProps) {
  const navigate = useNavigate();
  const { markDemoPurged } = useTrialOrientation();
  const { orgId } = useOrg();
  const [purging, setPurging] = useState(false);
  const [done, setDone] = useState(false);

  const handlePurge = async () => {
    if (!orgId) {
      toast.error("No organization found. Please try again.");
      return;
    }

    setPurging(true);
    try {
      // Purge demo-tagged records from each entity table.
      // Order matters for FK safety — children before parents.
      const { DEMO_ENTITY_TYPES } = await import("@/lib/trialOrientation");
      const errors: string[] = [];

      for (const table of DEMO_ENTITY_TYPES) {
        const { error } = await (supabase.from(table) as any)
          .delete()
          .eq("org_id", orgId)
          .contains("tags", [DEMO_DATA_TAG]);

        if (error) {
          console.error(`Failed to purge ${table}:`, error.message);
          errors.push(table);
        }
      }

      if (errors.length > 0) {
        toast.warning(`Demo data partially cleared. Some ${errors.join(", ")} records may remain.`);
      } else {
        toast.success("Demo data cleared! Let's set up your real organization.");
      }

      markDemoPurged();
      setDone(true);
    } catch (err: any) {
      toast.error(err.message || "Something went wrong during cleanup.");
    } finally {
      setPurging(false);
    }
  };

  const handleContinue = () => {
    onOpenChange(false);
    setDone(false);
    navigate("/");
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        {!done ? (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-destructive" />
                Switch to Your Real Organization
              </AlertDialogTitle>
              <AlertDialogDescription className="space-y-3">
                <p>
                  This will remove all sample/demo data from your workspace so you can start fresh with your real
                  organization.
                </p>
                <div className="bg-muted/50 rounded-lg p-3 space-y-1.5">
                  <p className="text-xs font-medium text-foreground">What will be removed:</p>
                  <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
                    <li>Sample members and companies</li>
                    <li>Sample events</li>
                    <li>Sample email campaigns</li>
                  </ul>
                </div>
                <div className="bg-primary/5 rounded-lg p-3 space-y-1.5">
                  <p className="text-xs font-medium text-foreground">What will be preserved:</p>
                  <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
                    <li>Your account and login</li>
                    <li>Your organization settings</li>
                    <li>Your trial access and time remaining</li>
                    <li>Your selected preferences</li>
                  </ul>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={purging}>
                Keep Exploring
              </Button>
              <Button onClick={handlePurge} disabled={purging} className="gap-1.5">
                {purging ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Clear Demo Data & Start Fresh
              </Button>
            </AlertDialogFooter>
          </>
        ) : (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-primary" />
                You're All Set
              </AlertDialogTitle>
              <AlertDialogDescription>
                Demo data has been cleared. Your workspace is now clean and ready for your real organization setup.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <Button onClick={handleContinue} className="gap-1.5">
                Set Up My Organization
                <ArrowRight className="w-4 h-4" />
              </Button>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}
